import React, { useState, useEffect, useCallback } from 'react';
import {
  Badge,
  Dropdown,
  Drawer,
  Empty,
  Spin,
  Button,
  Divider,
  Typography,
} from 'antd';
import {
  BellOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  FileSearchOutlined,
  SendOutlined,
  TrophyOutlined,
  ClockCircleOutlined,
  StopOutlined,
  DeleteOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/api.service';
import './NotificationBell.css';

const { Text } = Typography;

interface Notification {
  id: number;
  type: string;
  title: string;
  message: string;
  icon: string;
  color: string;
  link: string | null;
  read: boolean;
  created_at: string;
  metadata?: any;
}

const NotificationBell: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
  const navigate = useNavigate();

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => setIsMobile(window.innerWidth < 992), 150);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const [notifs, countData] = await Promise.all([
        apiService.getNotifications({ limit: 10 }),
        apiService.getNotificationsCount(),
      ]);
      setNotifications(notifs);
      setUnreadCount(countData.unread_count);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    // Poll every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const handleBellClick = () => {
    if (isMobile) {
      setDrawerOpen(true);
    }
    // Desktop dropdown is handled by Ant Design's trigger
  };

  const closeNotifications = () => {
    setDropdownOpen(false);
    setDrawerOpen(false);
  };

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.read) {
      await apiService.markNotificationRead(notification.id);
      setNotifications(prev =>
        prev.map(n => (n.id === notification.id ? { ...n, read: true } : n))
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    }

    if (notification.link) {
      closeNotifications();
      navigate(notification.link);
    }
  };

  const handleMarkAllRead = async () => {
    await apiService.markAllNotificationsRead();
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  };

  const handleDismiss = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    await apiService.dismissNotification(id);
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const handleViewAll = () => {
    closeNotifications();
    navigate('/notifications');
  };

  const getIcon = (type: string, icon?: string) => {
    const iconMap: Record<string, React.ReactNode> = {
      email_sent: <SendOutlined style={{ color: '#3b82f6' }} />,
      email_opened: <EyeOutlined style={{ color: '#10b981' }} />,
      email_clicked: <FileSearchOutlined style={{ color: '#8b5cf6' }} />,
      quote_accepted: <CheckCircleOutlined style={{ color: '#10b981' }} />,
      quote_rejected: <CloseCircleOutlined style={{ color: '#ef4444' }} />,
      opportunity_won: <TrophyOutlined style={{ color: '#f59e0b' }} />,
      opportunity_lost: <CloseCircleOutlined style={{ color: '#ef4444' }} />,
      reminder: <ClockCircleOutlined style={{ color: '#f59e0b' }} />,
      appointment_assigned: <CalendarOutlined style={{ color: '#EB2ADD' }} />,
      unavailability_created: <StopOutlined style={{ color: '#ef4444' }} />,
    };

    // Dynamic icon override from notification metadata
    if (icon) {
      const dynamicIcons: Record<string, React.ReactNode> = {
        SendOutlined: <SendOutlined style={{ color: '#3b82f6' }} />,
        StopOutlined: <StopOutlined style={{ color: '#ef4444' }} />,
      };
      if (dynamicIcons[icon]) return dynamicIcons[icon];
    }

    return iconMap[type] || <BellOutlined style={{ color: '#64748b' }} />;
  };

  const formatTime = (date: string) => {
    const now = new Date();
    const notifDate = new Date(date);
    const diff = now.getTime() - notifDate.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Ora';
    if (minutes < 60) return `${minutes}m fa`;
    if (hours < 24) return `${hours}h fa`;
    if (days < 7) return `${days}g fa`;
    return notifDate.toLocaleDateString('it-CH');
  };

  // Shared notification list content
  const notificationList = (
    <>
      {loading ? (
        <div className="notification-dropdown__loading">
          <Spin />
        </div>
      ) : notifications.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Nessuna notifica"
          style={{ padding: '24px 0' }}
        />
      ) : (
        <div className="notification-dropdown__list">
          {notifications.map(notification => (
            <div
              key={notification.id}
              className={`notification-item ${
                !notification.read ? 'notification-item--unread' : ''
              }`}
              onClick={() => handleNotificationClick(notification)}
            >
              <div className="notification-item__icon">
                {getIcon(notification.type, notification.icon)}
              </div>
              <div className="notification-item__content">
                <div className="notification-item__title">
                  {notification.title}
                </div>
                <div className="notification-item__message">
                  {notification.message}
                </div>
                <div className="notification-item__time">
                  {formatTime(notification.created_at)}
                </div>
              </div>
              <Button
                type="text"
                size="small"
                icon={<DeleteOutlined />}
                className="notification-item__dismiss"
                onClick={(e) => handleDismiss(e, notification.id)}
              />
            </div>
          ))}
        </div>
      )}
    </>
  );

  // Desktop dropdown content
  const dropdownContent = (
    <div className="notification-dropdown">
      <div className="notification-dropdown__header">
        <Text strong>Notifiche</Text>
        {unreadCount > 0 && (
          <Button
            type="link"
            size="small"
            onClick={handleMarkAllRead}
            icon={<CheckOutlined />}
          >
            Segna tutte come lette
          </Button>
        )}
      </div>

      <Divider style={{ margin: 0 }} />

      <div className="notification-dropdown__content">
        {notificationList}
      </div>

      {notifications.length > 0 && (
        <>
          <Divider style={{ margin: 0 }} />
          <div className="notification-dropdown__footer">
            <Button type="link" onClick={handleViewAll}>
              Vedi tutte le notifiche
            </Button>
          </div>
        </>
      )}
    </div>
  );

  // Bell button
  const bellButton = (
    <div className="notification-bell" onClick={isMobile ? handleBellClick : undefined}>
      <Badge count={unreadCount} size="small" offset={[-2, 2]}>
        <BellOutlined className="notification-bell__icon" />
      </Badge>
    </div>
  );

  return (
    <>
      {/* Desktop: use Dropdown */}
      {!isMobile ? (
        <Dropdown
          popupRender={() => dropdownContent}
          trigger={['click']}
          open={dropdownOpen}
          onOpenChange={setDropdownOpen}
          placement="bottomRight"
        >
          {bellButton}
        </Dropdown>
      ) : (
        bellButton
      )}

      {/* Mobile: use Drawer (bottom sheet) */}
      <Drawer
        title={null}
        placement="bottom"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        height="auto"
        className="notification-drawer"
        closable={false}
        styles={{
          header: { display: 'none' },
          body: { padding: 0 },
        }}
      >
        <div className="notification-drawer__content">
          {/* Handle bar */}
          <div className="notification-drawer__handle" />

          {/* Header */}
          <div className="notification-drawer__header">
            <Text strong className="notification-drawer__title">Notifiche</Text>
            {unreadCount > 0 && (
              <Button
                type="link"
                size="small"
                onClick={handleMarkAllRead}
                className="notification-drawer__mark-read"
              >
                Segna tutte come lette
              </Button>
            )}
          </div>

          {/* List */}
          <div className="notification-drawer__list">
            {notificationList}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="notification-drawer__footer">
              <Button type="primary" block onClick={handleViewAll}>
                Vedi tutte le notifiche
              </Button>
            </div>
          )}
        </div>
      </Drawer>
    </>
  );
};

export default NotificationBell;
